import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSchools } from "../../hooks/useSchools";
import SchoolCard from "../../components/SchoolCard";

export default function MySchoolsList() {
  const navigate = useNavigate();
  const { getMySchools, loading } = useSchools();

  const [mySchools, setMySchools] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const fetchMySchools = async () => {
      // Chỉ fetch nếu chưa load data
      if (dataLoaded) return;

      try {
        // Luôn lấy tất cả trường, không filter ở API
        const result = await getMySchools();

        if (result.success !== false) {
          setMySchools(result.schools || result);
          setDataLoaded(true);
        } else {
          setError(result.error || "Có lỗi xảy ra khi tải danh sách trường");
        }
      } catch (err) {
        setError("Đã xảy ra lỗi không mong muốn");
      }
    };

    fetchMySchools();
  }, [dataLoaded]); // Chỉ depend vào dataLoaded flag

  const handleRefresh = async () => {
    try {
      // Luôn lấy tất cả trường khi refresh
      const result = await getMySchools();

      if (result.success !== false) {
        setMySchools(result.schools || result);
        setError("");
      } else {
        setError(result.error || "Có lỗi xảy ra khi tải danh sách trường");
      }
    } catch (err) {
      setError("Đã xảy ra lỗi không mong muốn");
    }
  };

  const handleSchoolClick = (school) => {
    navigate(`/schools/${school._id}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Chờ duyệt", className: "status-badge--pending" },
      approved: { label: "Đã duyệt", className: "status-badge--approved" },
      rejected: { label: "Bị từ chối", className: "status-badge--rejected" },
    };

    const config = statusConfig[status] || { label: status, className: "" };
    return (
      <span className={`status-badge ${config.className}`}>{config.label}</span>
    );
  };

  const tabs = [
    { key: "all", label: "Tất cả", count: mySchools.length },
    {
      key: "pending",
      label: "Chờ duyệt",
      count: mySchools.filter((s) => s.status === "pending").length,
    },
    {
      key: "approved",
      label: "Đã duyệt",
      count: mySchools.filter((s) => s.status === "approved").length,
    },
    {
      key: "rejected",
      label: "Bị từ chối",
      count: mySchools.filter((s) => s.status === "rejected").length,
    },
  ];

  const filteredSchools =
    activeTab === "all"
      ? mySchools
      : mySchools.filter((school) => school.status === activeTab);
  return (
    <div className="my-schools">
      <div className="my-schools__header">
        <h3 className="my-schools__title">Trường do bạn tạo</h3>
        <p className="my-schools__subtitle">
          Quản lý và theo dõi trạng thái các trường bạn đã đóng góp
        </p>
      </div>

      {/* Status Tabs */}
      <div className="my-schools__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`my-schools__tab ${
              activeTab === tab.key ? "my-schools__tab--active" : ""
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="my-schools__tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="my-schools__loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách trường...</p>
        </div>
      ) : error ? (
        <div className="my-schools__error">
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn btn--primary">
            Thử lại
          </button>
        </div>
      ) : filteredSchools.length === 0 ? (
        <div className="my-schools__empty">
          <div className="my-schools__empty-icon">🏫</div>
          <h4>Chưa có trường nào</h4>
          <p>
            {activeTab === "all"
              ? "Bạn chưa tạo trường nào. Hãy đóng góp thông tin về trường học mà bạn biết!"
              : `Không có trường nào ở trạng thái "${
                  tabs.find((t) => t.key === activeTab)?.label
                }"`}
          </p>
          {activeTab === "all" && (
            <button
              onClick={() => navigate("/schools/create")}
              className="btn btn--primary"
            >
              Thêm trường mới
            </button>
          )}
        </div>
      ) : (
        <div className="my-schools__grid">
          {filteredSchools.map((school) => (
            <div key={school._id} className="my-schools__school-item">
              <div className="my-schools__school-status">
                {getStatusBadge(school.status)}
              </div>
              <SchoolCard
                school={{
                  ...school,
                  rating: school.ratings?.overall || 0,
                }}
                onClick={handleSchoolClick}
              />
              {school.status === "rejected" && school.rejectionReason && (
                <div className="my-schools__rejection-reason">
                  <strong>Lý do từ chối:</strong> {school.rejectionReason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
